/**
 * The release sitting's dialog bodies (13-12): the confirm, the per-chunk progress and the result.
 * Rendered inside the page's React Aria `ModalOverlay` / `Dialog` (the mock's dialog.confirm).
 *
 * No toast-undo for bulk (RESEARCH anti-pattern): an explicit confirm, then an honest summary.
 * ISS-212: when the note names the other verb ("Smoke test, retire" with Release), the confirm
 * asks "did you mean…?" first, with "Release anyway" / "Switch to Retire" (and the reverse). Focus
 * starts on Cancel then, never on either verb.
 * The result names every exception ("187 released · 2 changed by someone else · 1 to retry"),
 * so a partial failure never reads as success.
 */

import { useEffect, useState } from 'react';
import { Button, Heading } from 'react-aria-components';
import type { BulkProgress, BulkSummary } from '../../services/actionService';
import { isClean, mismatchQuestion, noteVerbMismatch, ownerBreakdown, pastDue, resultParts } from '../../lib/releaseFilters';
import { isRegulatoryDecision } from '../../lib/actionAuthority';
import type { ActionRow, OwnerRole } from '../../types/actionRegister';
import styles from './ReleasePage.module.css';

export type SittingVerb = 'release' | 'retire';

const ROLE_LABEL: Record<OwnerRole, string> = {
  Oversight: 'Oversight',
  CareManager: 'Care Manager',
  HRWHS: 'HR & WHS',
  Finance: 'Finance',
  Admin: 'Admin (Oversight)',
  Board: 'Board (Oversight)',
  QCAC: 'QCAC (Oversight)',
  Wellness: 'Wellness (Oversight)',
  DAG: 'DAG (Oversight)',
};

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// ---------------------------------------------------------------------------
// Confirm
// ---------------------------------------------------------------------------

interface ConfirmProps {
  verb: SittingVerb;
  rows: readonly ActionRow[];
  note: string;
  now: number;
  onCancel: () => void;
  onConfirm: () => void;
  /** ISS-212: "Switch to Retire" / "Switch to Release" — the confirm re-reads as the other verb. */
  onSwitch: (verb: SittingVerb) => void;
}

const VERB_WORD: Record<SittingVerb, { title: string }> = {
  release: { title: 'Release' },
  retire: { title: 'Retire' },
};

export function ReleaseConfirm({ verb, rows, note, now, onCancel, onConfirm, onSwitch }: ConfirmProps) {
  const n = rows.length;
  const late = pastDue(rows, now).length;
  const regulatory = rows.filter((r) => isRegulatoryDecision(r).regulatory).length;
  const roles = ownerBreakdown(rows);
  // ISS-212: the note names the other verb. Ask first; the rest of the confirm still shows below.
  const other = noteVerbMismatch(note, verb);
  return (
    <>
      <div className={styles.cb}>
        {other ? (
          <div className={styles.guard} role="alert">
            <Heading slot="title">{mismatchQuestion(other)}</Heading>
            <p>
              You pressed <b>{VERB_WORD[verb].title}</b>.{' '}
              {verb === 'release'
                ? 'Releasing sends each row to its owner with this note. Retiring closes it for good and cards no one.'
                : 'Retiring closes each row for good and cards no one. Releasing sends it to its owner with this note.'}
            </p>
          </div>
        ) : null}
        {verb === 'release' ? (
          <>
            {other ? (
              <h3 className={styles.guardSub}>Release {plural(n, 'row')} to their owners?</h3>
            ) : (
              <Heading slot="title">Release {plural(n, 'row')} to their owners?</Heading>
            )}
            <p>Each role below is sent a card at the next 15-minute poll:</p>
            <ul className={styles.breakdown} aria-label="Rows by owner role">
              {roles.map((r) => (
                <li key={r.value}>
                  <b>{r.count}</b> {ROLE_LABEL[r.value] ?? r.value}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <>
            {other ? (
              <h3 className={styles.guardSub}>Retire {plural(n, 'row')}?</h3>
            ) : (
              <Heading slot="title">Retire {plural(n, 'row')}?</Heading>
            )}
            <p>They leave every queue and are never carded. Each row keeps this note and records you.</p>
          </>
        )}
        <p className={styles.quoteLabel}>
          {verb === 'release' ? 'The note each owner reads in their card, word for word:' : 'The note on every row:'}
        </p>
        <blockquote className={styles.quote}>{note}</blockquote>
        {verb === 'release' && late > 0 ? (
          <p className={styles.warn}>
            {late === n ? (n === 1 ? 'It is' : `All ${n} are`) : `${late} ${late === 1 ? 'is' : 'are'}`} already past due and will
            escalate on the next 08:00 sweep.
          </p>
        ) : null}
        {regulatory > 0 ? (
          <p className={styles.warn}>
            {plural(regulatory, 'regulatory decision')} {regulatory === 1 ? 'is' : 'are'} in this batch.{' '}
            {verb === 'release'
              ? 'Released, they go to Oversight through the regulatory panel.'
              : 'Retiring one means nobody makes that call here.'}
          </p>
        ) : null}
        {verb === 'release' ? (
          <p className={styles.muted}>Each row records you as the releaser, with today's date.</p>
        ) : null}
      </div>
      <div className={styles.cf}>
        <Button className={`${styles.btn} ${styles.quiet}`} onPress={onCancel} autoFocus={other !== null}>
          Cancel
        </Button>
        {other ? (
          <>
            {/* Switching re-reads the confirm as the other verb; it never runs anything itself. */}
            <Button className={`${styles.btn} ${styles.line}`} onPress={() => onSwitch(other)}>
              Switch to {VERB_WORD[other].title}
            </Button>
            <Button className={`${styles.btn} ${verb === 'release' ? styles.go : styles.solemn}`} onPress={onConfirm}>
              {VERB_WORD[verb].title} anyway
            </Button>
          </>
        ) : (
          <Button className={`${styles.btn} ${verb === 'release' ? styles.go : styles.solemn}`} onPress={onConfirm} autoFocus>
            {verb === 'release' ? 'Release' : 'Retire'} {n}
          </Button>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Progress
// ---------------------------------------------------------------------------

export function ReleaseProgress({ verb, count, progress }: { verb: SittingVerb; count: number; progress: BulkProgress | null }) {
  const chunks = progress?.chunks ?? Math.max(1, Math.ceil(count / 20));
  const done = progress?.done ?? 0;
  const sending = Math.min(chunks, (progress?.chunk ?? 0) + 1);
  const pct = count > 0 ? Math.round((done / count) * 100) : 0;
  return (
    <div className={styles.cb}>
      <Heading slot="title">
        {verb === 'release' ? 'Releasing' : 'Retiring'} {plural(count, 'row')}
      </Heading>
      <div
        className={styles.bar}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={count}
        aria-valuenow={done}
        aria-label="Rows sent"
      >
        <i style={{ width: `${pct}%` }} />
      </div>
      <p className={styles.muted} aria-live="polite">
        {done >= count && progress
          ? 'Every batch is sent. Checking the replies…'
          : `Sending batch ${sending} of ${chunks}, 20 rows at a time · ${done} of ${count} sent`}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Result
// ---------------------------------------------------------------------------

const REFUSED: Record<string, string> = {
  'oversight-only': 'only Oversight can do this',
  'already-released': 'it was already released',
  terminal: 'it is already closed',
  'invalid-transition': 'its status no longer allows it',
  'note-required': 'the note was empty',
  'no-change': 'nothing would change',
  'unknown-verb': 'not an Action Register action',
};

function failedText(f: BulkSummary['failed'][number]): string {
  if (f.reason === 'throttled') return 'SharePoint asked us to slow down. Nothing was written; retrying is safe.';
  if (f.reason === 'no-response')
    return "SharePoint didn't confirm this one. Retrying is safe: if it did save, it comes back as changed by someone else.";
  return `This didn't save${f.status ? ` (${f.status})` : ''}. Nothing was written; retrying is safe.`;
}

interface ResultProps {
  verb: SittingVerb;
  summary: BulkSummary;
  /** How many rows were asked for. */
  asked: number;
  /** Epoch ms before which a retry would be refused again (Retry-After), or null. */
  retryAt: number | null;
  busy: 'retry' | 'audit' | null;
  /** Practice mode: the result is what live would do; nothing was saved. */
  practice: boolean;
  onRetry: () => void;
  onPostAudit: () => void;
}

export function ReleaseResult({ verb, summary: s, asked, retryAt, busy, practice, onRetry, onPostAudit }: ResultProps) {
  const counts = {
    verb,
    applied: s.applied.length,
    conflicts: s.conflicts.length,
    retried: s.retried,
    failed: s.failed.length,
    refused: s.refused.length,
    auditPending: s.auditPending.length,
  };
  const clean = isClean(counts, asked);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (retryAt === null) return;
    const t = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(t);
  }, [retryAt]);
  const wait = retryAt === null ? 0 : Math.max(0, Math.ceil((retryAt - now) / 1000));

  const auditRows = new Map<number, number>();
  for (const a of s.auditPending) auditRows.set(a.id, (auditRows.get(a.id) ?? 0) + 1);

  return (
    <>
      <div className={styles.cb}>
        <Heading slot="title">{clean ? 'Done' : 'Done, with some to look at'}</Heading>
        <p className={styles.result}>{resultParts(counts).join(' · ')}</p>
        {practice ? <p className={styles.muted}>Practice mode: this is what a live sitting would do. Nothing was saved.</p> : null}
        {!clean ? (
          <ul className={styles.exlist}>
            {s.conflicts.map((id) => (
              <li key={`c${id}`}>
                <span className={styles.mono}>ACT-{id}</span>
                <span>Someone else changed this row first. It's reloaded in the table for you to look at again.</span>
              </li>
            ))}
            {s.failed.map((f) => (
              <li key={`f${f.id}`}>
                <span className={styles.mono}>ACT-{f.id}</span>
                <span>{failedText(f)}</span>
              </li>
            ))}
            {s.refused.map((f) => (
              <li key={`r${f.id}`}>
                <span className={styles.mono}>ACT-{f.id}</span>
                <span>Not sent: {REFUSED[f.code] ?? f.code}.</span>
              </li>
            ))}
            {[...auditRows].map(([id, n]) => (
              <li key={`a${id}`}>
                <span className={styles.mono}>ACT-{id}</span>
                <span>
                  {verb === 'release' ? 'Released' : 'Retired'}, but {plural(n, 'audit row')} didn't post. Post them before you
                  close this.
                </span>
              </li>
            ))}
          </ul>
        ) : null}
        {s.conflicts.length || s.failed.length ? (
          <p className={styles.muted}>Those rows stay selected in the table, each with its reason.</p>
        ) : null}
      </div>
      <div className={styles.cf}>
        {s.auditPending.length ? (
          <Button className={`${styles.btn} ${styles.line}`} isDisabled={busy !== null} onPress={onPostAudit}>
            {busy === 'audit' ? 'Posting…' : `Post the ${plural(s.auditPending.length, 'audit row')}`}
          </Button>
        ) : null}
        {s.failed.length ? (
          <Button className={`${styles.btn} ${styles.line}`} isDisabled={busy !== null || wait > 0} onPress={onRetry}>
            {busy === 'retry' ? 'Retrying…' : wait > 0 ? `Retry in ${wait} s` : `Retry the ${s.failed.length}`}
          </Button>
        ) : null}
        <Button className={`${styles.btn} ${styles.go}`} slot="close" isDisabled={busy !== null} autoFocus>
          Done
        </Button>
      </div>
    </>
  );
}
